/**
 * Send to cashier and back, as the counter draws it.
 *
 * The server holds every rule: who a customer goes back to, when, and where. What
 * is here is only what the screen decides — whether to draw the button, what to
 * send, whose name to print — kept out of Counter.tsx so a test can run it.
 */
import type { Colleague, Placement } from './transfer'
import type { CounterView, Service, Ticket, TicketState } from './types'

/** The services Send to cashier may send to. `routed` is what this branch routes
 *  (GET /branches/{id}/services), so a cashier with no queue here is not offered. */
export function cashierServices(routed: readonly Service[]): Service[] {
  return routed.filter((s) => s.cashier && s.active)
}

/** Drawn while a customer is being served, at a desk whose profile allows a
 *  transfer to a service, and when this branch has a cashier — but not at the
 *  cashier's own desk: a customer whose current service is a cashier service is
 *  already there. */
export function offersSendToCashier(
  serving: boolean,
  mayTransfer: boolean,
  cashiers: readonly Service[],
  currentServiceId = '',
): boolean {
  if (currentServiceId !== '' && cashiers.some((s) => s.id === currentServiceId)) return false
  return serving && mayTransfer && cashiers.length > 0
}

/** A transfer's body. `outcome`, `marks` and `returnToMe` are sent only when
 *  given: the server refuses an outcome or marks while they are switched off. */
export function transferBody(
  serviceId: string,
  outcome: string,
  placed: { placement?: Placement },
  marks: readonly string[],
  reason: string,
  returnToMe: boolean,
): Record<string, unknown> {
  return {
    serviceId,
    ...(outcome ? { outcome } : {}),
    ...placed,
    ...(marks.length > 0 ? { marks: [...marks] } : {}),
    reason,
    ...(returnToMe ? { returnToMe: true } : {}),
  }
}

/** Who a customer owing a return goes back to, by name: from the colleagues list,
 *  else from the desk they are signed in at; '' when neither knows them. */
export function returnsToName(
  ticket: Pick<Ticket, 'return'>,
  colleagues: readonly Colleague[],
  counters: readonly CounterView[],
): string {
  const id = ticket.return?.staffId
  if (!id) return ''
  return colleagues.find((c) => c.id === id)?.displayName ?? counters.find((c) => c.staffId === id)?.staffName ?? ''
}

/** The At the cashier list says only whether the customer is with somebody. */
export function awayStage(state: TicketState): 'waiting' | 'serving' {
  return state === 'called' || state === 'serving' || state === 'wrapup' ? 'serving' : 'waiting'
}

/** My returned customers at the top of the set-aside list; everybody else keeps
 *  the order they came in. */
export function returnedFirst<T extends { ticket: Pick<Ticket, 'returned'>; mine: boolean }>(rows: readonly T[]): T[] {
  const back = (r: T) => r.mine && Boolean(r.ticket.returned)
  return [...rows.filter(back), ...rows.filter((r) => !back(r))]
}

/** How many customers closing this desk sends to the queue: my returned customers
 *  waiting in my own set-aside list go at once (the server's sendBackReturned),
 *  and the ones still at the cashier go there when the cashier finishes, unless I
 *  have opened a desk again by then. */
export function closingSendsToQueue(
  tickets: readonly Pick<Ticket, 'state' | 'returned' | 'parkScope' | 'poolStaffId'>[],
  desk: Pick<CounterView, 'away'> | undefined,
  staffId: string,
): number {
  const waiting = tickets.filter(
    (t) => t.state === 'parked' && t.returned && t.parkScope === 'staff' && t.poolStaffId === staffId,
  ).length
  return waiting + (desk?.away ?? []).length
}
